import React from 'react';
import {
    getTheme,
    IColumn,
    SpinButton,
    Stack,
    IconButton, Text, Icon} from '@fluentui/react';
import {generateShortDescription} from "../Product";
import { INormalizedProduct } from "../../models/NormalizedProduct";
import { FavoritesDisplay } from "../../components/Products/FavoritesDisplay";
import { InStockDisplay } from "../../components/Products/InStockDisplay";
import { CostDisplay } from "../../components/Products/CostDisplay";
import {IWorkspaceItem, IWorkspaceItemDetails} from "../../models/Workspace";
import AddToCollectionButton from '../../components/ProductDetails/AddToCollectionButton';
import VendorSourceSelection from "../../components/ProductTable/VendorSourceSelection";
import PriceEntry from "../../components/ProductTable/PriceEntry";
import MarkupEntry from "../../components/ProductTable/MarkupEntry";
import WorkspaceMarkupHeader from "../../components/ProductTable/WorkspaceMarkupHeader";
import VendorDisplay from "../../components/Products/VendorDisplay";
import {IVendor} from "../../models/Search";
import TaxCodeSelection from "../../components/ProductTable/TaxCodeSelection";
import TaxCodeHeader from "../../components/ProductTable/TaxCodeHeader";
import SourceHeader from "../../components/ProductTable/SourceHeader";
import ManufacturerFilterLink from "../../components/Search/ManufacturerFilterLink";
import ProductThumbnail from "../../components/Products/ProductThumbnail";
import ProductOpenLink from "../../components/Products/ProductOpenLink";
import PurchaseStatsCell from "../../components/Products/PurchaseStatsCell";
import BundleSourceSummary from "../../components/ProductBundles/BundleSourceSummary";
import TaxableSelection from "../../components/ProductTable/TaxableSelection";
import TaxableHeader from "../../components/ProductTable/TaxableHeader";
import PriceLevelSelection from "../../components/ProductTable/PriceLevelSelection";
import SortableColumnHeader from "../../components/ResultTable/SortableColumnHeader";
import QuantityEntry from "../../components/ProductTable/QuantityEntry";
import BestPriceDisplay from '../../components/Products/BestPriceDisplay';
import BestPriceColumnHeader from '../../components/ResultTable/BestPriceColumnHeader';
import CurrencyDisplay from '../../components/Currency/CurrencyDisplay';
import ProductNameDisplay from '../../components/Search/ProductNameDisplay'; 
import {IProductBundleRef} from "../../models/ProductBundles";
import {BundleInStockDisplay} from "../../components/ProductBundles/BundleInStockDisplay";
import ToggleField from "../../components/Common/ToggleField";
import CurrencyFormat from "react-currency-format";

export interface IProductColumnBase {
    basicColumn: IColumn;
}
export interface IProductColumn extends IProductColumnBase{
    onSearchTableRender?: (product: INormalizedProduct, selectedBundle?: IProductBundleRef) => React.ReactElement;
    onSearchTableHeaderRender?: (isDemo?: boolean) => React.ReactElement;
    onSearchTableExampleRender?: (product: INormalizedProduct) => React.ReactElement;
    /** Fluent passes (item, rowIndex, column); the index lets a column show the line's position. */
    onWorkspaceTableRender?: (product: IWorkspaceItem, index?: number) => React.ReactElement;
    onWorkspaceTableExampleRender?: (product: IWorkspaceItem) => React.ReactElement;
    refreshingVendors?: boolean;
}

export const COLUMN_PROPERTIES: {[key: string]: IProductColumn } = {
    //WS & Search
    'VENDOR_COLUMN': {
        basicColumn: {
            key: 'vendor',
            name: 'Vendor',
            minWidth: 100,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: false,
            isSorted: false,
            isSortedDescending: false,
            isMultiline: true,
        },
    },
    'DENSE_VENDOR_COLUMN': {
        basicColumn: {
            key: 'dense_vendor',
            name: 'Vendor',
            minWidth: 115,
            maxWidth: 115,
            isRowHeader: true,
            isResizable: false,
            isSorted: false,
            isSortedDescending: false,
            isMultiline: true,
        },
    },
    'name': {
        basicColumn: {
            key: 'name',
            name: 'Name',
            fieldName: 'name',
            // The grid squeezes columns toward their minWidth when space runs short (criteria panel
            // open, laptop screen). At 100px the name and its configuration bullets wrapped into a
            // tall, unreadable sliver; below this the grid scrolls sideways instead.
            minWidth: 240,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            data: 'string',
            isPadded: true,
            isMultiline: true
        },
        onSearchTableHeaderRender: (isDemo) => <SortableColumnHeader
            label='Name'
            field='name'
            criteriaFilter='name'
            isDemo={isDemo}
        />,
        onSearchTableRender: (product: INormalizedProduct) => (
            <ProductNameDisplay product={product} />
        )
        ,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div>{product.product ? product.product.name : product.adhoc?.name}</div>,
    },
    'msrp': {
        basicColumn: {
            key: 'msrp',
            name: 'MSRP',
            fieldName: 'msrp',
            minWidth: 100,
            maxWidth: 100,
        },
        onSearchTableRender: (product: INormalizedProduct) => <div className='figure-cell cell-neutral'><span className='figure-top' /><span className='figure-main' style={{fontWeight: 400}}><CurrencyDisplay value={product.msrp || undefined} /></span></div>,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div className='figure-cell cell-neutral'><span className='figure-top' /><span className='figure-main' style={{fontWeight: 400}}><CurrencyDisplay value={(product.product?.msrp ?? product.normalized?.msrp) || undefined} /></span></div>,
    },
    //WS &Search
    'image': {
        basicColumn: {
            key: 'image',
            name: '',
            fieldName: 'imageDisplay',
            minWidth: 72,
            maxWidth: 72,
        },
        onSearchTableRender: (product: INormalizedProduct) =>
            <ProductThumbnail src={product.image} alt={product.name} images={product.images} productId={product.id} />,
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            if(product?.product){
                return(<ProductThumbnail src={product?.normalized?.image ?? product.product.image} alt={product?.product.name} images={product?.normalized?.images ?? product?.product?.images} productId={product?.normalized?.id ?? product?.productId} />)
            }
            if(product?.adhoc?.image){
                return(<ProductThumbnail src={product?.adhoc?.image} alt={product.adhoc.name} />)
            }
            return <div></div>
        }
    },
    //WS & Search
    'availability': {
        basicColumn: {
            key: 'availability',
            name: 'Availability',
            fieldName: 'stockLabel',
            minWidth: 150,
            maxWidth: 150,
            isRowHeader: true,
        }
    },
    'type': {
        basicColumn: {
            key: 'type',
            name: 'Type',
            fieldName: 'type',
            minWidth: 200,
            maxWidth: 200,
            isRowHeader: true,
        }
    },
    //WS & Search
    'sortOrder': {
        basicColumn: {
            key: 'sortOrder',
            name: 'Item Number',
            fieldName: 'sortOrder',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
        },
        // The line's position in the grid (1-based); the stored sortOrder is often unset or sparse.
        onWorkspaceTableRender: (_product: IWorkspaceItem, index?: number) => ( <div>{(index ?? 0) + 1}</div>)
    },
    //WS & Search
    'cost': {
        basicColumn: {
            key: 'cost',
            name: 'Cost',
            fieldName: 'cost',
            minWidth: 100,
            maxWidth: 150,
            isMultiline: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <CostDisplay product={product} affirmative='text' openProductId={product.id} />,
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            if (product.normalized) {
                // Green figure (not fill) when the selected source has stock; the range and an always-included
                // fallback cost stay black, since those are not a sourced, in-stock price.
                return <CostDisplay product={product.normalized} source={product.details.source} wsItem={product} affirmative='text' openProductId={product.normalized.id ?? product.productId}/>
            }
            else if (product.adhoc?.cost) { return <CostDisplay wsItem={product} affirmative='text'/>}
            else if (product.isBundle) { return <CostDisplay wsItem={product} affirmative='text'/>}
            else return <div></div>
        },
    },
    //WS & Search
    'presentedPrice': {
        basicColumn: {
            key: 'presentedPrice',
            name: 'Presented Price',
            fieldName: 'presentedPrice',
            minWidth: 100,
            maxWidth: 150,
            isMultiline: false,
        },
        onSearchTableRender: () => <></>,
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            return (
            <Text variant='mediumPlus' style={{fontWeight: 'bold'}} >
                <CurrencyFormat
                    value={product.details.presentedPrice}
                    displayType="text"
                    thousandSeparator=","
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale={true}
                />
            </Text>
            )
        },
    },
    'internalPartNumber': {
        basicColumn: {
            key: 'internalPartNumber',
            name: 'Internal PN',
            fieldName: 'internalPartNumber',
            minWidth: 100,
            maxWidth: 150,
        }
    },
    //Search
    'favorite': {
        basicColumn: {
            key: 'favorite',
            name: '',
            fieldName: 'favorite',
            minWidth: 25,
            maxWidth: 25,
            isIconOnly: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <FavoritesDisplay product={product} />,
    },
    //Search
    'manufacturer': {
        basicColumn: {
            key: 'manufacturer',
            name: 'Manufacturer',
            fieldName: 'manufacturer',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            data: 'string',
            isPadded: true,
        },
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div>{product.product?.manufacturer}</div>,
        // Click the maker to narrow the search to it (until a manufacturer filter is already set).
        onSearchTableRender: (product: INormalizedProduct) => <ManufacturerFilterLink manufacturer={product.manufacturer} />,
        onSearchTableHeaderRender: (isDemo) => <SortableColumnHeader
            label='Manufacturer'
            field='manufacturer'
            isDemo={isDemo}
            />
    },
    //WS & Search
    'purchases': {
        basicColumn: {
            key: 'purchases',
            name: 'Ordered',
            fieldName: 'purchaseStats',
            minWidth: 70,
            maxWidth: 90,
            isResizable: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <PurchaseStatsCell stats={product.purchaseStats} productId={product.id} catalog={product.catalog} />,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <PurchaseStatsCell stats={product.normalized?.purchaseStats ?? product.product?.purchaseStats} productId={product.normalized?.id ?? product.productId} catalog={product.normalized?.catalog ?? product.catalog} />,
    },
    //Search
    'mfp': {
        basicColumn: {
            key: 'mfp',
            name: 'MFP',
            fieldName: 'manufacturerPartNumber',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <ProductOpenLink productId={product.id}>{product.manufacturerPartNumber}</ProductOpenLink>,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <ProductOpenLink productId={product.normalized?.id ?? product.productId}>{product.product?.manufacturerPartNumber}</ProductOpenLink>,
    },
    //Search
    'details': {
        basicColumn: {
            key: 'details',
            name: '',
            fieldName: 'details',
            minWidth: 100,
            maxWidth: 100,
        },
    },
    //Search
    'addWs': {
        basicColumn: {
            key: 'addWs',
            name: '',
            fieldName: 'addWs',
            minWidth: 100,
            maxWidth: 100,
        },
        onSearchTableRender: (product: INormalizedProduct, productBundle?: IProductBundleRef) => <AddToCollectionButton product={product} selectedBundle={productBundle}/>,
    },
    'best_price_base': {
        basicColumn: {
            key: 'best_price',
            name: 'Best Price',
            fieldName: 'best_price',
            minWidth: 100,
            maxWidth: 100,
        },
    },
    //WS
    'quantity': {
        basicColumn: {
            key: 'quantity',
            name: 'Quantity',
            fieldName: 'quantity',
            minWidth: 100,
            maxWidth: 100,
        }
    },
    //WS
    'actions': {
        basicColumn: {
            key: 'actions',
            name: '',
            fieldName: 'actions',
            // Up to four 32px icon buttons.
            minWidth: 100,
            maxWidth: 140,
            isResizable: true,
        },
    },
    //WS
    'price': {
        basicColumn: {
            key: 'price',
            name: 'Price',
            fieldName: 'price',
            minWidth: 125
        },
        onWorkspaceTableRender: (item) => <PriceEntry item={item} />
    },
    //WS
    'markup': {
        basicColumn: {
            key: 'markup',
            name: 'Markup',
            fieldName: 'markup',
            minWidth: 125,
            onRenderHeader: () => <WorkspaceMarkupHeader />,
        },
        onWorkspaceTableRender: (item) => <MarkupEntry item={item} />
    },
    //WS
    'listDiscount': {
        basicColumn: {
            key: 'listDiscount',
            name: 'List Price Discount',
            fieldName: 'listDiscount',
            minWidth: 100
        },
        onWorkspaceTableRender: () => <SpinButton step={1} defaultValue="0"/>
    },
    //WS
    'source': {
        basicColumn: {
            key: 'source',
            name: 'Source',
            fieldName: 'source',
            minWidth: 200,
            onRenderHeader: () => <SourceHeader/>
        },
        onWorkspaceTableRender: (item) => {
            if (item.normalized) { return <VendorSourceSelection item={item}/>}
            else if (item.isBundle && item.sourceEntityId) { return <BundleSourceSummary item={item}/>}
            else if (item.adhoc?.source) { return <div>{item.adhoc?.source}</div>}
            else { return <div></div>}
        },
    },
    //WS
    'taxRate': {
        basicColumn: {
            key: 'taxRate',
            name: 'Tax Code',
            fieldName: 'taxRate',
            // A code name plus its rate fits comfortably; the dropdown callout sizes itself.
            minWidth: 150,
            maxWidth: 220,
            isResizable: true,
            onRenderHeader: () => <TaxCodeHeader></TaxCodeHeader>
        },
        onWorkspaceTableRender: (item) => <TaxCodeSelection item={item} />
    },
    //WS
    'isTaxable': {
        basicColumn: {
            key: 'isTaxable',
            name: 'Taxable',
            fieldName: 'isTaxable',
            minWidth: 125,
            onRenderHeader: () => <TaxableHeader/>
        },
        onWorkspaceTableRender: (item) => <TaxableSelection item={item} />
    },
    'priceLevel': {
        basicColumn: {
            key: 'priceLevel',
            name: 'Price Level',
            fieldName: 'priceLevelPartNumber',
            minWidth: 200
        },
        onWorkspaceTableRender: (item) => <PriceLevelSelection item={item} />
    }
}


export const EXAMPLE_COLUMN_PROPERTIES: {[key: string]: IProductColumn } = {
    //WS & Search
    'VENDOR_COLUMN': {
        basicColumn: {
            key: 'vendor_example',
            name: 'Vendor',
            minWidth: 100,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: false,
            isSorted: false,
            isSortedDescending: false,
            isMultiline: true,
        },
    },
    'DENSE_VENDOR_COLUMN': {
        basicColumn: {
            key: 'dense_vendor_example',
            name: 'Vendor',
            minWidth: 115,
            maxWidth: 115,
            isRowHeader: true,
            isResizable: false,
            isSorted: false,
            isSortedDescending: false,
            isMultiline: true,
        },
    },
    'name': {
        basicColumn: {
            key: 'name_example',
            name: 'Name',
            fieldName: 'name',
            minWidth: 100,
            isRowHeader: true,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            data: 'string',
            isPadded: true,
            isMultiline: true
        },
        onSearchTableHeaderRender: () => <SortableColumnHeader
            label='Name'
            field='name_example'
            criteriaFilter='name'
            isDemo={true}
        />,
        onSearchTableRender: (product: INormalizedProduct) => {
            const shortDescription = generateShortDescription(product);
            return <div>
                {product.name} {product.isConfigurable && <span style={{color: getTheme().palette.themePrimary}}> (Configurable)</span>}
                {shortDescription && <p style={{color: 'grey'}}>
                    {shortDescription}
                </p>}
            </div>
        },
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div>{product.product ? product.product.name : product.adhoc?.name}</div>,
    },
    'msrp': {
        basicColumn: {
            key: 'msrp_example',
            name: 'MSRP',
            fieldName: 'msrp',
            minWidth: 100,
            maxWidth: 100,
        },
        onSearchTableRender: (product: INormalizedProduct) => <div className='figure-cell cell-neutral'><span className='figure-top' /><span className='figure-main' style={{fontWeight: 400}}><CurrencyDisplay value={product.msrp || undefined} /></span></div>,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div className='figure-cell cell-neutral'><span className='figure-top' /><span className='figure-main' style={{fontWeight: 400}}><CurrencyDisplay value={(product.product?.msrp ?? product.normalized?.msrp) || undefined} /></span></div>,
    },
    //WS &Search
    'image': {
        basicColumn: {
            key: 'image_example',
            name: '',
            fieldName: 'imageDisplay',
            minWidth: 72,
            maxWidth: 72,
        },
        onSearchTableRender: (product: INormalizedProduct) => (product.image
            ? <ProductThumbnail src={product.image} alt={product.name} images={product.images} productId={product.id} />
            : <div></div>),
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            if(product?.product?.image){
                return(<ProductThumbnail src={product?.normalized?.image} alt={product?.product.name} images={product?.normalized?.images ?? product?.product?.images} productId={product?.normalized?.id ?? product?.productId} />)
            }
            if(product?.adhoc?.image){
                return(<ProductThumbnail src={product?.adhoc?.image} alt={product.adhoc.name} />)
            }
            return <div></div>
        }
    },
    //WS & Search
    'presentedPrice': {
        basicColumn: {
            key: 'presentedPrice_example',
            name: 'Presented Price',
            fieldName: 'presentedPrice',
            minWidth: 100,
            maxWidth: 150,
            isMultiline: false,
        },
        onSearchTableRender: () => <></>,
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            return (
                <Text variant='mediumPlus' style={{fontWeight: 'bold'}} >
                    <CurrencyFormat
                        value={100}
                        displayType="text"
                        thousandSeparator=","
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                    />
                </Text>
            )
        },
    },
    //WS & Search
    'availability': {
        basicColumn: {
            key: 'availability_example',
            name: 'Availability',
            fieldName: 'stockLabel',
            minWidth: 150,
            maxWidth: 150,
            isRowHeader: true,
        }
    },
    'type': {
        basicColumn: {
            key: 'type_example',
            name: 'Type',
            fieldName: 'type',
            minWidth: 150,
            maxWidth: 150,
            isRowHeader: true,
        }
    },
    //WS & Search
    'sortOrder': {
        basicColumn: {
            key: 'sortOrder_example',
            name: 'Item Number',
            fieldName: 'sortOrder',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
        },
        // The line's position in the grid (1-based); the stored sortOrder is often unset or sparse.
        onWorkspaceTableRender: (_product: IWorkspaceItem, index?: number) => ( <div>{(index ?? 0) + 1}</div>)
    },
    //WS & Search
    'cost': {
        basicColumn: {
            key: 'cost_example',
            name: 'Cost',
            fieldName: 'cost',
            minWidth: 100,
            maxWidth: 150,
            isMultiline: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <CostDisplay product={product} affirmative='text' />,
        onWorkspaceTableRender: (product: IWorkspaceItem) => {
            if (product.normalized) {
                return <CostDisplay product={product.normalized} source={product.details.source} wsItem={product}/>
            }
            else if (product.adhoc?.cost) { return <CostDisplay wsItem={product}/>}
            else return <div></div>
        },
    },
    'internalPartNumber': {
        basicColumn: {
            key: 'internalPartNumber_example',
            name: 'Internal PN',
            fieldName: 'internalPartNumber',
            minWidth: 100,
            maxWidth: 150,
        }
    },
    //Search
    'favorite': {
        basicColumn: {
            key: 'favorite_example',
            name: '',
            fieldName: 'favorite',
            minWidth: 25,
            maxWidth: 25,
            isIconOnly: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <FavoritesDisplay product={product} isExample={true} />,
    },
    //Search
    'manufacturer': {
        basicColumn: {
            key: 'manufacturer_example',
            name: 'Manufacturer',
            fieldName: 'manufacturer',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
            data: 'string',
            isPadded: true,
        },
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div>{product.product?.manufacturer}</div>,
        onSearchTableHeaderRender: () => <SortableColumnHeader
            label='Manufacturer'
            field='manufacturer'
            isDemo={true}
        />
    },
    //WS & Search
    'purchases': {
        basicColumn: {
            key: 'purchases_example',
            name: 'Ordered',
            fieldName: 'purchaseStats',
            minWidth: 70,
            maxWidth: 90,
            isResizable: true,
        },
        onSearchTableRender: (product: INormalizedProduct) => <PurchaseStatsCell stats={product.purchaseStats} productId={undefined} />,
        onWorkspaceTableRender: (product: IWorkspaceItem) => <PurchaseStatsCell stats={product.normalized?.purchaseStats ?? product.product?.purchaseStats} productId={undefined} />,
    },
    //Search
    'mfp': {
        basicColumn: {
            key: 'mfp_example',
            name: 'MFP',
            fieldName: 'manufacturerPartNumber',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: true,
            isResizable: true,
        },
        onWorkspaceTableRender: (product: IWorkspaceItem) => <div>{product.product?.manufacturerPartNumber}</div>,
    },
    //Search
    'details': {
        basicColumn: {
            key: 'details_example',
            name: '',
            fieldName: 'details',
            minWidth: 100,
            maxWidth: 100,
        },
    },
    //Search
    'addWs': {
        basicColumn: {
            key: 'addWs_example',
            name: '',
            fieldName: 'addWs',
            minWidth: 100,
            maxWidth: 100,
        },
        onSearchTableRender: (product: INormalizedProduct, selectedBundle?: IProductBundleRef) => <AddToCollectionButton product={product} selectedBundle={selectedBundle} isExample={true}/>,
    },
    'best_price_base': {
        basicColumn: {
            key: 'best_price_example',
            name: 'Best Price',
            fieldName: 'best_price',
            minWidth: 100,
            maxWidth: 100,
        },
    },
    //WS
    'quantity': {
        basicColumn: {
            key: 'quantity_example',
            name: 'Quantity',
            fieldName: 'quantity',
            minWidth: 100,
            maxWidth: 100,
        }
    },
    //WS
    'actions': {
        basicColumn: {
            key: 'actions_example',
            name: '',
            fieldName: 'actions',
            // Up to four 32px icon buttons.
            minWidth: 100,
            maxWidth: 140,
            isResizable: true,
        },
    },
    //WS
    'price': {
        basicColumn: {
            key: 'price_example',
            name: 'Price',
            fieldName: 'price',
            minWidth: 125
        },
        onWorkspaceTableRender: (item) => <PriceEntry item={item} isExample={true} />
    },
    //WS
    'markup': {
        basicColumn: {
            key: 'markup_example',
            name: 'Markup',
            fieldName: 'markup',
            minWidth: 125,
            onRenderHeader: () => <WorkspaceMarkupHeader isExample={true} />,
        },
        onWorkspaceTableRender: (item) => <MarkupEntry item={item} />
    },
    //WS
    'listDiscount': {
        basicColumn: {
            key: 'listDiscount_example',
            name: 'List Price Discount',
            fieldName: 'listDiscount',
            minWidth: 100
        },
        onWorkspaceTableRender: () => <SpinButton step={1} defaultValue="0"/>
    },
    //WS
    'source': {
        basicColumn: {
            key: 'source_example',
            name: 'Source',
            fieldName: 'source',
            minWidth: 200
        },
        onWorkspaceTableRender: (item) => {
            if (item.normalized) { return <VendorSourceSelection item={item} isExample={true}/>}
            else if (item.adhoc?.source) { return <div>{item.adhoc?.source}</div>}
            else { return <div></div>}
        },
    },
    //WS
    'taxRate': {
        basicColumn: {
            key: 'taxRate_example',
            name: 'Tax Code',
            fieldName: 'taxRate',
            // A code name plus its rate fits comfortably; the dropdown callout sizes itself.
            minWidth: 150,
            maxWidth: 220,
            isResizable: true,
            onRenderHeader: () => <TaxCodeHeader></TaxCodeHeader>
        },
        onWorkspaceTableRender: (item) => <TaxCodeSelection item={item} isExample={true} />
    },
    //WS
    'isTaxable': {
        basicColumn: {
            key: 'isTaxable_example',
            name: 'Taxable',
            fieldName: 'isTaxable',
            minWidth: 125,
            onRenderHeader: () => <TaxableHeader isExample={true}/>
        },
        onWorkspaceTableRender: (item) => <TaxableSelection item={item} isExample={true}/>
    },
    'priceLevel': {
        basicColumn: {
            key: 'priceLevel_example',
            name: 'Price Level',
            fieldName: 'priceLevelPartNumber',
            minWidth: 200
        },
        onWorkspaceTableRender: (item) => <PriceLevelSelection item={item} isExample={true} />
    }
}
const EXAMPLE_VENDOR: IVendor = {
    name: 'Example Product',
    onHand: 10,
    inStock: true,
    cost: 134.99,
    vendorPartNumber: 'abc123',
    vendorName: 'Example Vendor',
    priceBreaks: [
        {
            quantity: 1,
            price: 134.99
        },
        {
            quantity: 5,
            price: 129.99
        },
        {
            quantity: 10,
            price: 124.99
        },
    ]
}

export const renderSearchColumns = (selectedColumns: string[], refreshing: boolean, isExample = false, refreshingVendors: string[] = [], selectedBundle?: IProductBundleRef) : IColumn[] => {
    const columns: IColumn[] = [];
    for (const field of selectedColumns) {
        if(COLUMN_PROPERTIES[field]) {
            const tempCol: IColumn = isExample ? EXAMPLE_COLUMN_PROPERTIES[field].basicColumn : COLUMN_PROPERTIES[field].basicColumn;
            if(isExample){
                tempCol.onColumnClick = undefined;
            }
            if(field === "availability"){
                tempCol.onRender = (product: INormalizedProduct) => {
                    return(<InStockDisplay refreshing={refreshing} product={product} multiLine loadingWarehouses={refreshing} isExample={isExample} />)
                };
            } else if (field === 'details') {
                // Details is always appended as an action column by ResultTable and is no longer a
                // user-customizable column. Skip any legacy saved config that still lists it so we
                // don't render a duplicate blank column.
                continue;
            } else if(COLUMN_PROPERTIES[field].onSearchTableRender){
                tempCol.onRender = isExample ? (product: INormalizedProduct) => EXAMPLE_COLUMN_PROPERTIES[field].onSearchTableRender?.(product, selectedBundle) : (product: INormalizedProduct) => COLUMN_PROPERTIES[field].onSearchTableRender?.(product, selectedBundle);
            }

            if (COLUMN_PROPERTIES[field].onSearchTableHeaderRender) {
                tempCol.onRenderHeader = () => {
                    if(isExample){
                        return EXAMPLE_COLUMN_PROPERTIES[field]?.onSearchTableHeaderRender?.(true) ?? <div></div>;
                    }
                    return COLUMN_PROPERTIES[field].onSearchTableHeaderRender?.() ?? <div></div>;
                };
            }

            columns.push(tempCol);

        } else if (field.startsWith('VENDOR:')) {
            const tempCol: IColumn = {
                ...(isExample ? EXAMPLE_COLUMN_PROPERTIES['VENDOR_COLUMN'].basicColumn : COLUMN_PROPERTIES['VENDOR_COLUMN'].basicColumn),
                key: field,
                name: field.split(':')[1],
                onRenderHeader: () => <SortableColumnHeader
                    label={field.split(':')[1]}
                    vendor={field.split(':')[1]}
                    field='vendor'
                    isDemo={isExample}
                />,
            };
            tempCol.onRender = (product: INormalizedProduct) => {
                const vendors = product.vendors?.filter(v => v.vendorName == field.split(':')[1]);
                const vendorName = (vendors != null && vendors?.length > 0) ? vendors[0].vendorName : null;
                const exampleVendor: IVendor[] = [EXAMPLE_VENDOR];
                return <VendorDisplay
                    product={product}
                    refreshing={refreshing || (vendorName != null && refreshingVendors.includes(vendorName))}
                    loadingWarehouses={false}
                    vendor={isExample ? exampleVendor : vendors}
                    isDemo={isExample}
                />;
            }
            columns.push(tempCol);
        } else if (field.startsWith('DENSE_VENDOR:')) {
            const tempCol: IColumn = {
                ...COLUMN_PROPERTIES['DENSE_VENDOR_COLUMN'].basicColumn,
                key: field,
                name: field.split(':')[1],
                onRenderHeader: () => <SortableColumnHeader
                    label={field.split(':')[1]}
                    vendor={field.split(':')[1]}
                    field='vendor'
                    isDemo={isExample}
                />,
            };
            tempCol.onRender = (product: INormalizedProduct) => {
                const vendors = product.vendors?.filter(v => v.vendorName == field.split(':')[1]);
                const vendorName = (vendors != null && vendors?.length > 0) ? vendors[0].vendorName : null;
                const exampleVendor: IVendor[] = [EXAMPLE_VENDOR];
                return <VendorDisplay
                    product={product}
                    refreshing={refreshing || (vendorName != null && refreshingVendors.includes(vendorName))}
                    loadingWarehouses={false}
                    vendor={isExample ? exampleVendor : vendors}
                    dense
                />;
            }
            columns.push(tempCol);
        } else if (field === 'best_price') {
            columns.push({
                ...COLUMN_PROPERTIES['best_price_base'].basicColumn,
                onRender: (product) => <BestPriceDisplay product={product} refreshingVendors={refreshingVendors} isExample={isExample} affirmative='text' />,
                onRenderHeader: () => <BestPriceColumnHeader isDemo={isExample} />
            });
        }
    }
    return columns
}

export const renderWorkspaceColumns = (
    selectedColumns: string[],
    refreshingVendors: boolean,
    isExample: boolean,
    onDetailsChange: (product: IWorkspaceItem, details: IWorkspaceItemDetails) => void,
    onDelete: (workspaceProductId: string) => void,
    onDetailsClick: (product: IWorkspaceItem) => void,
    onAdhocClick: (product: IWorkspaceItem) => void,
    onBundleClick?: (product: IWorkspaceItem) => void,
    sorter?: (column: IColumn) => void,
    onConvertCustomerAdded?: (product: IWorkspaceItem) => void,
    onBundleConfigClick?: (product: IWorkspaceItem) => void,
    onOrderSelectionClick?: (product: IWorkspaceItem) => void
): IColumn[] => {
    const columns: IColumn[] = [];
    for (const field of selectedColumns) {
        if(COLUMN_PROPERTIES[field]) {
            const tempCol: IColumn = isExample ? EXAMPLE_COLUMN_PROPERTIES[field].basicColumn : COLUMN_PROPERTIES[field].basicColumn;
            tempCol.onColumnClick = (ev, column) =>  {if(sorter && !isExample){sorter(column)}}
            if(field === "quantity"){
                tempCol.onRender = (product: IWorkspaceItem) => 
                    <QuantityEntry
                        item={product}
                        onChange={(v) => isExample ? undefined: onDetailsChange(product, { ...product.details, quantity: v })}
                    />
                
            }
            if(field === "type"){
                tempCol.onRender = (product: IWorkspaceItem) => {
                    const type = product.product ? 'Product' : product.isBundle ? 'Bundle' : product.adhoc ? 'Ad-hoc' : 'Unknown';
                    if(product.userAdded){
                        return <div>{type}<ToggleField inline={true} checked={product.userAdded} label={'Customer Added'} onChange={(e, v) => onConvertCustomerAdded?.(product)}/></div>
                    }
                    return <div>{type}</div>
                }

            }
            else if(field === "availability") {
                tempCol.onColumnClick = undefined
                tempCol.onRender = (product: IWorkspaceItem) => {
                    const stock = product.normalized
                        ? <InStockDisplay isExample={isExample} hideWarehouses refreshing={refreshingVendors} product={product.normalized} multiLine loadingWarehouses={refreshingVendors} />
                        : product.isBundle && product.sourceEntityId
                            ? <BundleInStockDisplay
                                item={product}
                                onIncludeAnyway={isExample ? undefined : () => onDetailsChange(product, {...product.details, alwaysAvailable: true})}/>
                            : null;
                    if (product.details?.alwaysAvailable !== true) return stock;
                    // The prepared-cart "include anyway" override: a small pill on the line, and its x is
                    // the way back out of it. Colors match the workspace header pills. Centered like a stock count.
                    return <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                        {stock}
                        <span
                            title='This line is included in the prepared cart even when no vendor can source it'
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 2,
                                padding: '1px 4px 1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                background: '#e2e9cb', color: '#43571a', whiteSpace: 'nowrap'
                            }}>
                            <Icon iconName='Accept' style={{fontSize: 9}} />
                            Always included
                            {!isExample &&
                                <span
                                    role='button'
                                    title='Stop including this line when it cannot be sourced'
                                    aria-label='Remove the always-included override'
                                    onClick={(e) => { e.stopPropagation(); onDetailsChange(product, {...product.details, alwaysAvailable: false}); }}
                                    style={{display: 'inline-flex', alignItems: 'center', padding: '0 2px', cursor: 'pointer', opacity: 0.8}}>
                                    <Icon iconName='Cancel' style={{fontSize: 9}} />
                                </span>}
                        </span>
                    </div>;
                };
            }
            else if(field === "actions"){
                const theme = getTheme();
                tempCol.onColumnClick = undefined
                tempCol.onRender = (product: IWorkspaceItem) => {
                    if (product.product) {
                        return <Stack horizontal tokens={{childrenGap: 5}}>
                            <IconButton
                                iconProps={{iconName: 'Delete'}}
                                onClick={isExample ? undefined : () => {
                                    if (onDelete)
                                        onDelete(product.id);
                                }}
                            />
                            <IconButton 
                                iconProps={{iconName: 'EntryView'}} 
                                onClick={() => isExample? undefined : onDetailsClick(product)}
                            />
                        </Stack>
                    }
                    else if (product.isBundle && product.sourceEntityId) {
                        return <Stack horizontal tokens={{childrenGap: 5}}>
                            <IconButton
                                iconProps={{iconName: 'Delete'}}
                                onClick={isExample ? undefined : () => {
                                    if (onDelete)
                                        onDelete(product.id);
                                }}
                            />
                            <IconButton
                                title={'Bundle details'}
                                ariaLabel={'Bundle details'}
                                iconProps={{iconName: 'EntryView'}}
                                onClick={() => isExample ? undefined: onBundleClick?.(product)}
                            />
                            <IconButton
                                title={'Configure this bundle line (options and quantities for this workspace)'}
                                ariaLabel={'Configure Bundle'}
                                iconProps={{iconName: 'Edit'}}
                                onClick={() => isExample ? undefined : onBundleConfigClick?.(product)}
                            />
                            {(product.bundleDefinition?.products?.length ?? 0) > 0 &&
                                <IconButton
                                    title={'Order Selection Details'}
                                    ariaLabel={'Order Selection Details'}
                                    iconProps={{iconName: 'BulletedList2'}}
                                    onClick={() => isExample ? undefined : onOrderSelectionClick?.(product)}
                                />}
                        </Stack>
                    }
                    else if (product.adhoc) {
                         return <Stack horizontal tokens={{childrenGap: 5}}>
                             <IconButton 
                                 iconProps={{iconName: 'Delete'}}
                                 onClick={isExample ? undefined : () => {
                                     if (onDelete)
                                         onDelete(product.id);
                                 }}
                             />
                             <IconButton 
                                    iconProps={{iconName: 'EntryView'}}
                                    onClick={() => isExample ? undefined: onAdhocClick(product)}
                             />
                        </Stack>
                    }
                    else return null;
                }
            }
            else if(isExample && EXAMPLE_COLUMN_PROPERTIES[field].onWorkspaceTableRender){
                tempCol.onRender = EXAMPLE_COLUMN_PROPERTIES[field].onWorkspaceTableRender;
            }
            else if(!isExample && COLUMN_PROPERTIES[field].onWorkspaceTableRender){
                tempCol.onRender = COLUMN_PROPERTIES[field].onWorkspaceTableRender;
            }
            columns.push(tempCol);

        }
    }
    return columns
}